/**
 * WeaveIn.ai Platform - Form Instance Editor Component
 * Interface for completing and submitting form instances
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import useFormInstances from '@/hooks/useFormInstances';
import { formsAPI, FormInstance, FormTemplate, FormFieldSchema, FormSectionSchema, FormSubmissionData, MyFormPermissions, BILLED_STATUSES, amendRequirements } from '@/services/api/forms';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { useResolvedFormTheme } from '@/hooks/useResolvedFormTheme';
import { hasTheme, themeToCssVars } from './formTheme';
import { spanClassFor } from './fieldWidth';
import { Save, Send, X, Clock, CheckCircle, AlertCircle, Eye, EyeOff, DollarSign, PenTool, Printer, Download, AlertTriangle } from 'lucide-react';
import { BillingCaptureForm } from '../billing/BillingCaptureForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FORMS_GHOST_BTN, FORMS_MUTED, FORMS_PRIMARY_BTN, FORMS_SECONDARY_BTN } from './formsUi';
import { SignaturePad, SignatureResult } from '@/components/signatures/SignaturePad';
import { SignatureDisplay } from './SignatureDisplay';
import { defaultSignerName, isSignatureImage, lockedSignatureDateFieldIds, readSignatureRecord, recordFromPad, signatureDateFieldId, signatureMetaKey } from './signatureFields';
import { RepeatableGroupRenderer } from './RepeatableGroupRenderer';
import { normalizeOptions } from './fieldOptions';
import { pickDerivedValues } from './formInstanceFields';
import { isStaffBound, isStaffMultiSelect, useOrgStaffOptions } from './staffOptions';
import { StaffMultiSelect, toStringArray } from './StaffMultiSelect';
import { isFieldRequired, isFieldVisible } from './fieldVisibility';
import { renderFormElement, FieldTip, FORM_ELEMENT_TYPES } from './FormElements';
import { NeedSuggestions } from './NeedSuggestions';
import { buildFormPrintHtml, printInNewWindow } from './formPrintBuilder';
import { toDateInputValue, toDateTimeLocalInputValue } from '@/utils/dateTime';

import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useOptionalAuth } from '@/contexts/AuthContext';
import {
  AUTOSAVE_INTERVAL_CHOICES,
  describeAutoSaveInterval,
  resolveAutoSave,
} from '@/constants/formAutoSave';
interface FormInstanceEditorProps {
  instanceId?: number;
  enrollmentId?: number;
  templateId?: number;
  memberId?: number;
  programId?: number;
  organizationId?: number;
  onClose?: () => void;
  onSubmit?: (instance: FormInstance) => void;
}

export const FormInstanceEditor: React.FC<FormInstanceEditorProps> = ({
  instanceId,
  enrollmentId,
  templateId,
  memberId,
  programId,
  organizationId,
  onClose,
  onSubmit,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { instances, loading, error, createInstance, getInstance, submitInstance, getHistory } = useFormInstances({
    enrollment_id: enrollmentId,
    autoFetch: false,
  });

  const [currentInstance, setCurrentInstance] = useState<FormInstance | null>(null);
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  // The org's resolved brand theme for this form. FormRenderer ignores an
  // undefined theme, so an org with no design template renders as before.
  const formTheme = useResolvedFormTheme(template?.design_template_id);
  // Drafting is the surface people actually write progress notes on, so it
  // carries the org's brand too — not just the submitted record. `themed` is
  // false when the org has authored no design template, and every themed style
  // below is then skipped, so an unbranded org renders exactly as before.
  const themed = hasTheme(formTheme);

  // Form state is owned by react-hook-form. The hook holds all field values
  // (including arrays-of-objects for repeatable groups via useFieldArray) and
  // validation errors. We still keep a useState mirror for the existing field-
  // type renderers below so this migration stays focused — `handleFieldChange`
  // writes to both, and reads still go through the mirror. The new
  // `RepeatableGroupRenderer` reads/writes via the RHF FormContext.
  const form = useForm<Record<string, any>>({ defaultValues: {}, shouldUnregister: false });
  const [fieldData, setFieldData] = useState<Record<string, any>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Amendment of a SUBMITTED record (TRUS-361). Gated on a per-org function grant
  // (`forms.amend_submitted`), not on a role name — each org draws this line itself, so
  // the client has to ask rather than infer. `amending` is the explicit opt-in: a submitted
  // note stays read-only until someone deliberately chooses to amend it.
  // TRUS-362 adds a second, stronger tier: once a note is BILLED to a managed care plan,
  // ordinary amendment is closed. Editing needs `forms.amend_billed` and the audit note
  // becomes mandatory — "restricted after billing, editable during QA with audit notes
  // captured". Which tier applies is decided by `amendRequirements`, shared with the backend.
  const [perms, setPerms] = useState<MyFormPermissions | null>(null);
  const [amending, setAmending] = useState(false);
  const [amendmentReason, setAmendmentReason] = useState('');
  const [billingBusy, setBillingBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    formsAPI
      .getMyFormPermissions()
      .then((p) => { if (!cancelled) setPerms(p); })
      .catch(() => { /* fail closed — no amend affordance */ });
    return () => { cancelled = true; };
  }, []);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  // TRUS-498. Auto-save used to be a hard-coded 30s timer behind a checkbox that
  // reset to "on" every time this component mounted, so nobody — admin or user —
  // could change it, and turning it off never stuck. The cadence now resolves
  // from the template's settings (the admin's default) and the signed-in user's
  // own preference, and the control below writes that preference back.
  //
  // Auth is optional here: the preference is a convenience, and this component
  // also renders where there is no AuthProvider (the member portal). Without one
  // the cadence simply falls back to the template's setting.
  const auth = useOptionalAuth();
  const preferences = auth?.preferences ?? null;
  const autoSave = useMemo(
    () => resolveAutoSave(template?.settings, preferences),
    [template?.settings, preferences]
  );
  const [savingPreference, setSavingPreference] = useState(false);

  // Staff-bound fields (e.g. "Staff Attendees") carry no static options — their
  // choices are the org's live staff list. Loaded once here and injected into
  // the option-driven renderers below (TRUS-414). Previously only the bespoke
  // OutreachActivityForm loaded these, so a staff-bound field opened through
  // My Forms rendered as an empty picker.
  const staffOptions = useOrgStaffOptions();

  // Signature pad dialog state for form signature fields
  const [formSignatureDialogOpen, setFormSignatureDialogOpen] = useState(false);
  const [formSignatureFieldId, setFormSignatureFieldId] = useState<string | null>(null);

  // Hard-copy tracking state
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const [trackingAlert, setTrackingAlert] = useState<string | null>(null);
  const [hardCopyWarning, setHardCopyWarning] = useState(false);
  const [hardCopyEnabledByAnyVersion, setHardCopyEnabledByAnyVersion] = useState(false);

  // Has the initial load finished? This is NOT the same thing as the hook's
  // `loading`, and the difference was visible to users as a flash of "Form Not
  // Found" before the form appeared. `loading` belongs to `useFormInstances`
  // and covers only the instance fetch: it starts out false (autoFetch is off),
  // so the first render already fell through to the empty state, and it goes
  // back to false the moment the instance resolves — while `loadInstance` is
  // still awaiting the SEPARATE template fetch, which is a whole round trip
  // with `template` still null. Both windows rendered the error. This flag
  // spans the entire `loadInstance` call, so the empty state can only be
  // reached once the load has genuinely settled and produced nothing.
  const [bootstrapping, setBootstrapping] = useState(true);

  // Load instance or create new one
  useEffect(() => {
    let current = true;
    setBootstrapping(true);
    loadInstance().finally(() => {
      // Ignore a load that a prop change has already superseded, so its
      // completion cannot clear the flag out from under the newer one.
      if (current) setBootstrapping(false);
    });
    return () => { current = false; };
  }, [instanceId, templateId]);

  // Auto-save on the resolved cadence if dirty (TRUS-498).
  useEffect(() => {
    if (!autoSave.enabled || !isDirty || !currentInstance || currentInstance.status !== 'draft') {
      return;
    }

    const autoSaveTimer = setInterval(() => {
      handleSaveDraft(true);
    }, autoSave.seconds * 1000);

    return () => clearInterval(autoSaveTimer);
  }, [autoSave.enabled, autoSave.seconds, isDirty, currentInstance]);

  // A 10-minute cadence means up to 10 minutes of unsaved work, where 30 seconds
  // meant almost none — so the safety net that was never needed before is needed
  // now. Closing the tab mid-note is exactly the situation a longer gap creates,
  // and without this the work would go with no warning at all.
  useEffect(() => {
    if (!isDirty || !currentInstance || currentInstance.status !== 'draft') {
      return;
    }

    const warnOnUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Browsers show their own wording; a non-empty returnValue is what triggers it.
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', warnOnUnload);
    return () => window.removeEventListener('beforeunload', warnOnUnload);
  }, [isDirty, currentInstance]);

  const loadInstance = async () => {
    if (instanceId) {
      // Load existing instance
      const instance = await getInstance(instanceId);
      if (instance) {
        setCurrentInstance(instance);
        setFieldData(instance.field_data || {});
        form.reset(instance.field_data || {});

        // Load template
        const tmpl = await formsAPI.getFormTemplate(instance.form_template_id);
        setTemplate(tmpl);

        // Check if any version of this template has hard-copy tracking enabled
        // (user may have enabled it on a newer version after this instance was created)
        checkHardCopyAcrossVersions(tmpl);

        // Initialize collapsed sections
        const collapsed = new Set<string>();
        tmpl.sections.forEach((section) => {
          if (section.collapsed_by_default) {
            collapsed.add(section.section_id);
          }
        });
        setCollapsedSections(collapsed);
      }
    } else if (templateId && enrollmentId) {
      // Create new instance
      const tmpl = await formsAPI.getFormTemplate(templateId);
      setTemplate(tmpl);
      checkHardCopyAcrossVersions(tmpl);

      const newInstance = await createInstance({
        form_template_id: templateId,
        enrollment_id: enrollmentId,
      });

      if (newInstance) {
        setCurrentInstance(newInstance);
        setFieldData({});
        form.reset({});

        // Initialize collapsed sections
        const collapsed = new Set<string>();
        tmpl.sections.forEach((section) => {
          if (section.collapsed_by_default) {
            collapsed.add(section.section_id);
          }
        });
        setCollapsedSections(collapsed);
      }
    }
  };

  const loadHistory = async () => {
    if (currentInstance) {
      const hist = await getHistory(currentInstance.id);
      setHistory(hist);
      setShowHistory(true);
    }
  };

  const validateField = (field: FormFieldSchema, value: any): string | null => {
    // Requiredness can depend on another answer (TRUS-566 #4): IFR's billing fields are
    // mandatory only when Encounter Type is 'Billable'. isFieldRequired resolves that the
    // same way the backend does, so the inline prompt and the submit response agree —
    // a form saying "optional" while the API 422s is the TRUS-361 failure.
    if (isFieldRequired(field, template?.sections, fieldData) && !value) {
      return `${field.label} is required`;
    }

    if (field.validation) {
      // Number validation
      if (field.field_type === 'number') {
        const numValue = parseFloat(value);
        if (field.validation.min !== undefined && numValue < field.validation.min) {
          return `${field.label} must be at least ${field.validation.min}`;
        }
        if (field.validation.max !== undefined && numValue > field.validation.max) {
          return `${field.label} must be at most ${field.validation.max}`;
        }
      }

      // Text validation (pattern, min/max length)
      if (field.field_type === 'text' || field.field_type === 'textarea') {
        if (field.validation.minLength && value.length < field.validation.minLength) {
          return `${field.label} must be at least ${field.validation.minLength} characters`;
        }
        if (field.validation.maxLength && value.length > field.validation.maxLength) {
          return `${field.label} must be at most ${field.validation.maxLength} characters`;
        }
        if (field.validation.pattern) {
          const regex = new RegExp(field.validation.pattern);
          if (!regex.test(value)) {
            return field.validation.patternMessage || `${field.label} format is invalid`;
          }
        }
      }

      // Date validation
      if (field.field_type === 'date' || field.field_type === 'datetime') {
        const dateValue = new Date(value);
        if (field.validation.minDate && dateValue < new Date(field.validation.minDate)) {
          return `${field.label} must be after ${field.validation.minDate}`;
        }
        if (field.validation.maxDate && dateValue > new Date(field.validation.maxDate)) {
          return `${field.label} must be before ${field.validation.maxDate}`;
        }
      }
    }

    return null;
  };

  // Returns the map of field_id -> error message (empty = valid). Returning the
  // map, not just a boolean, lets the caller NAME the offending fields and jump
  // to the first one instead of a generic "fix the errors" alert that leaves the
  // user hunting for a red line that may be off-screen or in a collapsed section
  // (TRUS-751).
  const validateForm = (): Record<string, string> => {
    if (!template) return {};

    const errors: Record<string, string> = {};

    template.sections.forEach((section) => {
      section.fields.forEach((field) => {
        // A hidden conditional field cannot be required — skip both required
        // and shape validation so admins don't get blocked by fields the user
        // can't see.
        if (!isFieldVisible(field, template.sections, fieldData)) return;
        const value = fieldData[field.field_id];
        const error = validateField(field, value);
        if (error) {
          errors[field.field_id] = error;
        }
      });
    });

    setValidationErrors(errors);
    return errors;
  };

  // Expand the section holding the first invalid field (if collapsed) and scroll
  // it into view, so a blocked submit takes the user to what they must fix rather
  // than leaving them on the submit button with the error off-screen (TRUS-751).
  const focusFirstInvalidField = (errors: Record<string, string>) => {
    if (!template) return;
    for (const section of template.sections) {
      const bad = section.fields.find((f) => errors[f.field_id]);
      if (!bad) continue;
      setCollapsedSections((prev) => {
        if (!prev.has(section.section_id)) return prev;
        const next = new Set(prev);
        next.delete(section.section_id);
        return next;
      });
      // Let the (possibly just-expanded) field render before scrolling to it.
      setTimeout(() => {
        document
          .getElementById(`field-${bad.field_id}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
  };

  // Build a validation alert that NAMES the offending fields. The messages
  // already read "<Label> is required" (validateField), so listing them tells
  // the user exactly what to fix instead of a generic prompt (TRUS-751).
  const summarizeValidationErrors = (errors: Record<string, string>): string => {
    const messages = Object.values(errors);
    const shown = messages.slice(0, 6);
    const extra = messages.length - shown.length;
    return (
      'Please fix the following before you can submit:\n\n• ' +
      shown.join('\n• ') +
      (extra > 0 ? `\n• …and ${extra} more` : '')
    );
  };

  // TRUS-763: signatures. A draft or an amendment signs in place (saved with the
  // form). A submitted record signs through POST /instances/{id}/signatures, which
  // adds ONLY the signature and its record without re-running submit side effects.
  // Each signature field keeps its own record, so a member and a care manager who
  // sign on different days each get their own date.
  const canEditSignatures = currentInstance?.status === 'draft' || amending;
  const openSignaturePad = (fieldId: string) => {
    setFormSignatureFieldId(fieldId);
    setFormSignatureDialogOpen(true);
  };
  const signatureFieldBeingSigned = useMemo(
    () =>
      formSignatureFieldId
        ? (template?.sections || [])
            .flatMap((section) => section.fields || [])
            .find((f) => f.field_id === formSignatureFieldId) || null
        : null,
    [formSignatureFieldId, template],
  );
  const lockedSignatureDates = useMemo(
    () => lockedSignatureDateFieldIds(template?.sections, fieldData),
    [template, fieldData],
  );
  const saveSignature = async (fieldId: string, result: SignatureResult) => {
    if (!currentInstance) return;
    const record = recordFromPad(result);
    const sigField = (template?.sections || []).flatMap((s) => s.fields || []).find((f) => f.field_id === fieldId);
    const dateFieldId = signatureDateFieldId(sigField);
    if (canEditSignatures) {
      // Each signature fills ITS OWN date field; the server confirms the date on save.
      setFieldData((prev) => ({
        ...prev,
        [fieldId]: result.signature_data,
        [signatureMetaKey(fieldId)]: record,
        ...(dateFieldId ? { [dateFieldId]: record.signed_on } : {}),
      }));
      return;
    }
    try {
      const res = await formsAPI.addSignature(currentInstance.id, {
        field_id: fieldId,
        signature_data: result.signature_data,
        signer_name: record.signer_name ?? undefined,
        signature_type: record.signature_type ?? undefined,
        consent_accepted: record.consent_accepted ?? undefined,
        signed_local_date: record.signed_local_date ?? undefined,
      });
      setFieldData((prev) => ({
        ...prev,
        [fieldId]: result.signature_data,
        [signatureMetaKey(fieldId)]: res.signature,
        ...(res.date_field_id ? { [res.date_field_id]: res.signature.signed_on } : {}),
      }));
    } catch (err: any) {
      alert(err?.response?.data?.detail || t('forms.signature_save_failed', 'The signature could not be saved. Please try again.'));
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldData({ ...fieldData, [fieldId]: value });
    // Mirror into RHF so the FormContext (used by RepeatableGroupRenderer via
    // useFieldArray) stays in sync when admins mix repeatable groups with
    // legacy useState-driven fields.
    form.setValue(fieldId, value, { shouldDirty: true, shouldValidate: false });
    setIsDirty(true);

    if (validationErrors[fieldId]) {
      const newErrors = { ...validationErrors };
      delete newErrors[fieldId];
      setValidationErrors(newErrors);
    }
  };

  // Read the merged field_data (legacy + repeatable groups) as the source of
  // truth for save/submit/print. Repeatable group values live only in RHF state.
  const collectFieldData = (): Record<string, any> => {
    return { ...fieldData, ...form.getValues() };
  };

  /**
   * Persist the user's own auto-save choice (TRUS-498).
   *
   * Deliberately fire-and-forget as far as the form is concerned: this is a
   * preference, not the member's data, and a preferences call that fails must not
   * interrupt someone mid-assessment. The resolved cadence simply stays on whatever
   * it was — the previous behaviour — and the next change can retry.
   */
  const saveAutoSavePreference = async (change: {
    form_autosave_enabled?: boolean;
    form_autosave_interval_seconds?: number;
  }) => {
    // No AuthProvider above us (widget bundles, some tests) — nothing to persist to.
    if (!auth) return;

    setSavingPreference(true);
    try {
      // Turning auto-save on without ever having picked a cadence should keep the
      // one already in effect, otherwise the user silently reverts to the default.
      const patch =
        change.form_autosave_enabled === true && preferences?.form_autosave_interval_seconds == null
          ? { ...change, form_autosave_interval_seconds: autoSave.seconds }
          : change;

      await auth.updatePreferences(patch);
    } catch (err) {
      console.error('Could not save auto-save preference:', err);
    } finally {
      setSavingPreference(false);
    }
  };

  /** Merge back the scores the backend just calculated. See pickDerivedValues. */
  const absorbDerivedValues = (saved: any) => {
    const derived = pickDerivedValues(template?.sections, saved?.field_data);
    if (Object.keys(derived).length > 0) {
      setFieldData((prev) => ({ ...prev, ...derived }));
    }
  };

  const handleSaveDraft = async (isAutoSave: boolean = false) => {
    if (!currentInstance) return;

    setSaving(true);
    try {
      const result = await submitInstance(currentInstance.id, {
        field_data: collectFieldData(),
        status: 'draft',
      });

      absorbDerivedValues(result);
      setIsDirty(false);
      if (!isAutoSave) {
        alert('Draft saved successfully');
      }
    } catch (err) {
      console.error('Error saving draft:', err);
      if (!isAutoSave) {
        alert('Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  // Save an AMENDMENT to an already-submitted record (TRUS-361). Keeps the instance's
  // submitted status — an amendment does not reopen it as a draft — and carries the reason,
  // which the backend records on the form_instance_history row alongside who changed it.
  const handleSaveAmendment = async () => {
    if (!currentInstance) return;
    const amendErrors = validateForm();
    if (Object.keys(amendErrors).length > 0) {
      focusFirstInvalidField(amendErrors);
      alert(summarizeValidationErrors(amendErrors));
      return;
    }
    const reason = amendmentReason.trim();
    setSaving(true);
    try {
      const result = await submitInstance(currentInstance.id, {
        field_data: collectFieldData(),
        status: currentInstance.status,
        amendment_reason: reason || undefined,
      });
      absorbDerivedValues(result);
      setIsDirty(false);
      setAmending(false);
      setAmendmentReason('');
      alert('Amendment saved. The change is recorded in this form’s history.');
      if (onSubmit && result) onSubmit(result);
    } catch (err: any) {
      console.error('Error saving amendment:', err);
      // A 403 here means the org has not granted this role the amend function.
      const detail = err?.response?.data?.detail || err?.message;
      alert(detail || 'Failed to save the amendment');
    } finally {
      setSaving(false);
    }
  };

  // Save ONLY the narrative on an already-submitted record (TRUS-566 #3). Keeps the
  // instance's status — the billing went out and stays out — and sends no amendment
  // reason, because finishing a note that was always going to be written is not a
  // correction to justify. The backend grants this exemption only when the change is
  // confined to a field flagged `editable_after_submit`, and still records it in history
  // as `note_completed`; if anything else drifted it answers 403/422 and the alert below
  // surfaces the backend's own words rather than a guess.
  const handleSaveNote = async () => {
    if (!currentInstance) return;
    setSaving(true);
    try {
      const result = await submitInstance(currentInstance.id, {
        field_data: collectFieldData(),
        status: currentInstance.status,
      });
      absorbDerivedValues(result);
      setIsDirty(false);
      alert('Note saved. The billing on this record is unchanged.');
      if (onSubmit && result) onSubmit(result);
    } catch (err: any) {
      console.error('Error saving note:', err);
      alert(err?.response?.data?.detail || err?.message || 'Failed to save the note');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentInstance) return;

    // Validate form. Name the fields and jump to the first one: Category sits
    // above Encounter Type and can be off-screen or in a collapsed section, so a
    // generic "fix the errors" alert left LCMs hunting for an invisible red line
    // and the blocked submit looked like the note "reverted to draft" (TRUS-751).
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      focusFirstInvalidField(errors);
      alert(summarizeValidationErrors(errors));
      return;
    }

    if (!window.confirm('Submit this form? You will not be able to edit it after submission.')) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitInstance(currentInstance.id, {
        field_data: collectFieldData(),
        status: 'submitted',
      });

      absorbDerivedValues(result);

      if (result?.contact_reminders_acknowledged) {
        const { toast } = await import('sonner');
        toast.success('Form submitted successfully', {
          description: 'Monthly contact reminders have been automatically acknowledged.',
          duration: 4000,
        });
      } else {
        alert('Form submitted successfully');
      }
      setIsDirty(false);

      if (onSubmit && result) {
        onSubmit(result);
      }
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      // Surface the backend's reason — a conditionally-required field 422s with a
      // detail that NAMES the field (e.g. "Category ... is required.", TRUS-566).
      // Dropping it for a generic "Failed to submit form" is what made a blocked
      // submit look like a silent revert to draft (TRUS-751).
      const detail = err?.response?.data?.detail || err?.message;
      alert(detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId);
    } else {
      newCollapsed.add(sectionId);
    }
    setCollapsedSections(newCollapsed);
  };

  const calculateCompletionPercentage = (): number => {
    if (!template) return 0;

    let totalFields = 0;
    let completedFields = 0;

    template.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.field_type === 'section_header') return; // Skip headers
        // Display-only elements (panel headings, computed readouts, badges) hold
        // no answer, so counting them would cap completion below 100% on a form
        // the LCM has actually finished.
        if ((FORM_ELEMENT_TYPES as readonly string[]).includes(field.field_type)) return;
        if (!isFieldVisible(field, template.sections, fieldData)) return; // Hidden conditional fields don't count
        totalFields++;
        if (fieldData[field.field_id]) {
          completedFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  };

  const renderField = (field: FormFieldSchema) => {
    // Visibility is driven by field config (visibility_rules) via the shared
    // evaluator — same logic as the form preview and public intake page.
    if (!isFieldVisible(field, template?.sections, fieldData)) return null;

    const value = fieldData[field.field_id] || '';
    const hasError = !!validationErrors[field.field_id];

    // Settling a record normally locks every input. A field the org flagged
    // `editable_after_submit` is the exception: IFR's LCMs submit the billing so the claim
    // can go, and finish the narrative afterwards (TRUS-566 #3). The API already permits
    // exactly that edit and still records it in history — leaving the box disabled here
    // would be a form saying "read-only" while the backend accepts the write, which is
    // TRUS-361 with the sides swapped.
    // `validation.read_only` marks a field the form only DISPLAYS — plan-owned
    // data such as the ECM Member Snapshot (TRUS-571). It holds even on a draft,
    // and it outranks `editable_after_submit`, which widens who may edit a
    // settled record rather than making a display field writable.
    const fieldLocked =
      (field as any).validation?.read_only === true ||
      (currentInstance?.status !== 'draft' && !(field as any).editable_after_submit) ||
      // TRUS-763: a signature date is filled and locked when that signature was made
      // digitally; on paper the LCM enters it.
      lockedSignatureDates.has(field.field_id);

    // Staff-bound fields use the live org staff list as their options; every
    // other field uses whatever static options the template carries.
    const fieldOptions = isStaffBound(field.field_id, (field as any).options_source)
      ? staffOptions
      : normalizeOptions(field.options);

    // Repeatable / structured field group — delegates to its own renderer
    // backed by useFieldArray inside the surrounding FormProvider.
    if (field.field_type === 'field_group' || (field as any).field_group_id) {
      return (
        <RepeatableGroupRenderer
          field={field}
          disabled={fieldLocked}
        />
      );
    }

    // Presentational element types (TRUS-571 Layer 2). Mounted from the SAME
    // module as FormRenderer so the draft and the submitted record cannot show
    // different things — this editor and FormRenderer had already drifted on
    // divider/spacer/multiselect, and the required asterisk (TRUS-566 #4) was
    // exactly that bug. Returns null for anything it does not own.
    {
      const element = renderFormElement(field, fieldData[field.field_id]);
      if (element) {
        return (
          <div style={{ gridColumn: '1 / -1' }}>
            {field.label &&
              field.field_type !== 'panel_heading' &&
              field.field_type !== 'inline_alert' && (
                <label
                  className="block text-gray-700 dark:text-gray-300"
                  style={{ fontSize: '12px', fontWeight: 500, marginBottom: '3px' }}
                >
                  {field.label}
                  {field.help_text ? <FieldTip text={field.help_text} /> : null}
                </label>
              )}
            {element}
          </div>
        );
      }
    }

    // Section header (non-input)
    if (field.field_type === 'section_header') {
      return (
        <div style={{ gridColumn: '1 / -1', marginTop: '10px', marginBottom: '5px' }}>
          <h4 className="text-gray-900 dark:text-white" style={{ margin: 0, fontSize: '15px' }}>{field.label}</h4>
          {field.description && (
            <p className="text-gray-600 dark:text-gray-400" style={{ margin: '3px 0 0 0', fontSize: '12px' }}>{field.description}</p>
          )}
        </div>
      );
    }

    return (
      <div className="form-group">
        <label>
          {field.label}
          {isFieldRequired(field, template?.sections, fieldData) && (
            <span className="required">*</span>
          )}
          {/* Same tip as FormRenderer, from the same component, opt-in via
              settings.help_as_tip — the draft and the submitted record must show
              the LCM the same guidance. */}
          {field.help_text && (field as any).settings?.help_as_tip && (
            <FieldTip text={field.help_text} />
          )}
        </label>
        {field.description && (
          <div className="text-gray-600 dark:text-gray-400" style={{ fontSize: '12px', marginBottom: '5px' }}>{field.description}</div>
        )}

        {/* Text Input */}
        {field.field_type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            disabled={fieldLocked}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* Textarea */}
        {field.field_type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            disabled={fieldLocked}
            rows={4}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* Number */}
        {field.field_type === 'number' && (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            disabled={fieldLocked}
            min={field.validation?.min}
            max={field.validation?.max}
            step={field.validation?.step}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* Date */}
        {field.field_type === 'date' && (
          <input
            type="date"
            value={toDateInputValue(value)}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            disabled={fieldLocked}
            min={field.validation?.minDate}
            max={field.validation?.maxDate}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* Time */}
        {field.field_type === 'time' && (
          <input
            type="time"
            value={value}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            disabled={fieldLocked}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* DateTime */}
        {field.field_type === 'datetime' && (
          <input
            type="datetime-local"
            value={toDateTimeLocalInputValue(value)}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            disabled={fieldLocked}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* Select */}
        {field.field_type === 'select' && (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            disabled={fieldLocked}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          >
            <option value="">-- Select --</option>
            {fieldOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {/* Radio */}
        {field.field_type === 'radio' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {fieldOptions.map((opt) => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={field.field_id}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                  disabled={fieldLocked}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {/* Staff people-picker — a multi-select, not a checkbox group.
            The org staff list is unbounded, so rendering it as checkboxes put
            every promotora on screen before the next question (TRUS-414). */}
        {isStaffMultiSelect(field.field_id, field.field_type) && (
          <StaffMultiSelect
            options={staffOptions}
            value={toStringArray(value)}
            onChange={(next) => handleFieldChange(field.field_id, next)}
            disabled={fieldLocked}
            hasError={hasError}
            placeholder="Select staff…"
            emptyHint="No staff found for this organization"
          />
        )}

        {/* A checkbox with no options is a single yes/no box, as in FormRenderer.
            Drawn as a group it rendered nothing, so IFR's "Member declined to
            answer this section" boxes could not be ticked (TRUS-571). */}
        {field.field_type === 'checkbox' && fieldOptions.length === 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={value === true || value === 'true' || value === '1'}
              onChange={(e) => handleFieldChange(field.field_id, e.target.checked)}
              disabled={fieldLocked}
            />
            <span>{field.placeholder || 'Yes'}</span>
          </label>
        )}

        {/* Checkbox / multi-select (both render as a checkbox group) */}
        {(field.field_type === 'multiselect' || (field.field_type === 'checkbox' && fieldOptions.length > 0)) &&
          !isStaffMultiSelect(field.field_id, field.field_type) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {fieldOptions.map((opt) => {
              const checkedValues = Array.isArray(value) ? value : [];
              return (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    value={opt.value}
                    checked={checkedValues.includes(opt.value)}
                    onChange={(e) => {
                      let newValues = [...checkedValues];
                      if (e.target.checked) {
                        newValues.push(opt.value);
                      } else {
                        newValues = newValues.filter((v) => v !== opt.value);
                      }
                      handleFieldChange(field.field_id, newValues);
                    }}
                    disabled={fieldLocked}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* File Upload */}
        {field.field_type === 'file' && (
          <div>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // In production: Upload file to server and store URL
                  handleFieldChange(field.field_id, file.name);
                }
              }}
              disabled={fieldLocked}
            />
            {value && <div className="text-gray-600 dark:text-gray-400" style={{ fontSize: '12px', marginTop: '5px' }}>Uploaded: {value}</div>}
          </div>
        )}

        {/* Signature — integrated with SignaturePad. TRUS-763: shows the stored image
            with ITS OWN date and signer, treats legacy "Signed"/"Yes" text as unsigned,
            and can be signed while a draft or while amending. */}
        {field.field_type === 'signature' && (
          <div>
            <SignatureDisplay
              value={value}
              record={readSignatureRecord(fieldData, field.field_id)}
              onAdd={canEditSignatures ? () => openSignaturePad(field.field_id) : undefined}
            />
            {canEditSignatures && isSignatureImage(value) && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 mt-2 text-xs font-medium rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                onClick={() => openSignaturePad(field.field_id)}
              >
                <PenTool className="w-3 h-3" />
                {t('forms.re_sign', 'Re-sign')}
              </button>
            )}
          </div>
        )}

        {/* Rich Text */}
        {field.field_type === 'rich_text' && (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            disabled={fieldLocked}
            rows={6}
            style={{ borderColor: hasError ? '#ef4444' : undefined }}
          />
        )}

        {/* Help Text — suppressed when settings.help_as_tip puts it in the `i`
            glyph beside the label instead (TRUS-571), so it never shows twice. */}
        {field.help_text && !(field as any).settings?.help_as_tip && (
          <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '11px', marginTop: '3px' }}>{field.help_text}</div>
        )}

        {/* Validation Error */}
        {hasError && (
          <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <AlertCircle className="w-3 h-3" />
            {validationErrors[field.field_id]}
          </div>
        )}
      </div>
    );
  };

  // ========================================================================
  // PRINT / DOWNLOAD WITH HARD-COPY TRACKING
  // ========================================================================

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  };

  /**
   * Check if any version of this template (by name) has hard-copy tracking enabled.
   * This handles the case where the user enabled tracking on a new version but
   * existing form instances still reference the old template_id.
   */
  const checkHardCopyAcrossVersions = async (tmpl: FormTemplate) => {
    // Direct check first
    if (tmpl.settings?.requires_hard_copy_tracking) {
      setHardCopyEnabledByAnyVersion(true);
      return;
    }
    // Check all templates with same name for any version that has it enabled
    try {
      const allTemplates = await formsAPI.listFormTemplates({
        program_id: tmpl.program_id || undefined,
      });
      const sameNameTemplates = allTemplates.filter(
        (t) => t.name === tmpl.name
      );
      const anyEnabled = sameNameTemplates.some(
        (t) => t.settings?.requires_hard_copy_tracking === true
      );
      setHardCopyEnabledByAnyVersion(anyEnabled);
    } catch {
      // Fallback to just the current template's setting
    }
  };

  const hasHardCopyTracking = template?.settings?.requires_hard_copy_tracking === true || hardCopyEnabledByAnyVersion;

  /**
   * Get or create a tracking number for this form instance.
   * If one already exists, reuse it (prevents incrementing on every click).
   */
  const ensureTrackingNumber = async (action: string): Promise<string | null> => {
    // Already have one cached — reuse it
    if (trackingNumber) return trackingNumber;

    if (!hasHardCopyTracking) {
      setHardCopyWarning(true);
      setTimeout(() => setHardCopyWarning(false), 5000);
      return null;
    }

    if (!currentInstance || !memberId) return null;

    try {
      // Check if tracking already exists for this form instance
      const listRes = await fetch(
        getWeaveinApiUrl(`/api/hard-copy-tracking?member_id=${memberId}&page_size=50`),
        { credentials: 'include', headers: getAuthHeaders() },
      );
      if (listRes.ok) {
        const existing = await listRes.json();
        const match = existing.find?.((r: any) => r.form_instance_id === currentInstance.id);
        if (match) {
          setTrackingNumber(match.tracking_number);
          setTrackingAlert(`Tracking # ${match.tracking_number}`);
          setTimeout(() => setTrackingAlert(null), 6000);
          return match.tracking_number;
        }
      }

      // No existing — create new
      const res = await fetch(getWeaveinApiUrl('/api/hard-copy-tracking'), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          form_instance_id: currentInstance.id,
          target_type: 'member',
          target_id: memberId,
          program_id: programId || undefined,
          enrollment_id: enrollmentId || undefined,
          notes: `${action}: ${template?.name}`,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setTrackingNumber(data.tracking_number);
        setTrackingAlert(`Tracking # ${data.tracking_number} assigned`);
        setTimeout(() => setTrackingAlert(null), 6000);

        // Mark as printed
        try {
          await fetch(getWeaveinApiUrl(`/api/hard-copy-tracking/${data.id}/print`), {
            method: 'POST', credentials: 'include', headers: getAuthHeaders(),
            body: JSON.stringify({}),
          });
        } catch { /* non-blocking */ }

        return data.tracking_number;
      }
    } catch { /* non-blocking */ }
    return null;
  };

  const buildPrintableHTML = (tn: string | null): string => {
    if (!template || !currentInstance) return '';
    return buildFormPrintHtml({
      template,
      mode: 'filled',
      fieldData,
      trackingNumber: tn,
      memberName: currentInstance.member_name,
      status: currentInstance.status,
    });
  };

  const createTrackingAndPrint = async () => {
    const tn = await ensureTrackingNumber('Printed');
    printInNewWindow(buildPrintableHTML(tn));
  };

  const handleDownloadForm = async () => {
    const tn = await ensureTrackingNumber('Downloaded');
    printInNewWindow(buildPrintableHTML(tn));
  };

  if (loading || bootstrapping) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <LoadingSpinner size="lg" />
        <p className={`text-sm ${FORMS_MUTED}`}>{t('forms.loading_form', 'Loading form...')}</p>
      </div>
    );
  }

  if (!currentInstance || !template) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <h3 className="text-lg font-semibold text-ink">{t('forms.form_not_found', 'Form Not Found')}</h3>
        <p className={`text-sm ${FORMS_MUTED}`}>{t('forms.unable_to_load_form_instance', 'Unable to load form instance')}</p>
        {onClose && (
          <Button variant="secondary" className={`mt-2 ${FORMS_SECONDARY_BTN}`} onClick={onClose}>
            {t('forms.close', 'Close')}
          </Button>
        )}
      </div>
    );
  }

  const completionPercentage = calculateCompletionPercentage();
  const isSubmittedRecord = currentInstance.status !== 'draft';
  // Which tier of amendment this record needs, per the rule shared with the backend.
  const amendReq = amendRequirements(currentInstance.status);
  const isBilled = (BILLED_STATUSES as readonly string[]).includes(
    (currentInstance.status || '').toLowerCase(),
  );
  // Does the caller hold the grant THIS record requires? A billed note needs
  // amend_billed — holding only amend_submitted must not open it.
  const canAmendThis = !amendReq
    ? true
    : amendReq.function === 'forms.amend_billed'
      ? !!perms?.can_amend_billed
      : !!perms?.can_amend_submitted;
  const reasonRequired = !!amendReq?.reasonRequired;
  // A submitted record is read-only UNTIL someone with the org's amend grant explicitly
  // chooses to amend it. Opening it for editing has to be a deliberate act, not the default
  // — otherwise a signed clinical note becomes silently editable by anyone who can open it.
  const isReadOnly = isSubmittedRecord && !amending;
  // A settled record still offers a save when the template carries a field the org flagged
  // editable after submit — otherwise the note would be typeable and unsaveable, which is
  // worse than leaving it locked (TRUS-566 #3).
  const postSubmitFieldIds = (template?.sections || []).flatMap((section) =>
    (section.fields || [])
      .filter((f) => (f as any).editable_after_submit)
      .map((f) => f.field_id),
  );
  const canSaveNoteOnly = isSubmittedRecord && !amending && postSubmitFieldIds.length > 0;
  const statusLabel = isBilled
    ? (currentInstance.status || '').toLowerCase() === 'billed_qa'
      ? t('formsEditor.status_billed_qa', 'Billed + QA')
      : t('formsEditor.status_billed', 'Billed')
    : currentInstance.status;
  const statusVariant: 'success' | 'warning' | 'info' =
    currentInstance.status === 'draft' ? 'warning' : isBilled ? 'info' : 'success';

  const changeBillingStatus = async (next: 'billed' | 'billed_qa' | 'submitted') => {
    if (!currentInstance?.id) return;
    setBillingBusy(true);
    try {
      const res = await formsAPI.setBillingStatus(currentInstance.id, next);
      setCurrentInstance({ ...currentInstance, status: res.status } as FormInstance);
      setAmending(false);
    } catch (err: any) {
      alert(err?.response?.data?.detail || t('formsEditor.billing_status_failed', 'Could not change the billing status'));
    } finally {
      setBillingBusy(false);
    }
  };

  return (
    <FormProvider {...form}>
    {/* data-tsw-screen names this view for the support widget. A form filled for
        a member (a progress note, an assessment) renders OVER the member profile,
        whose heading comes first in the DOM — so a ticket raised from here used
        to be recorded against the member id instead of the form. */}
    <div
      // `wf-themed` scopes the brand overrides in members.css onto this
      // editor's .form-group inputs and labels. Those inputs carry no
      // className of their own — they are styled by a stylesheet rule — so a
      // scoped block is what themes them without touching eleven elements.
      className={`field-mapping-container forms-plain${themed ? ' wf-themed' : ''}`}
      style={themed ? themeToCssVars(formTheme) : undefined}
      data-tsw-screen={`Form: ${template.name}${currentInstance.member_name ? ` (${currentInstance.member_name})` : ''}`}
    >
      {/* Header — design-system chrome. The form BODY below keeps the org's
          brand theme (themeToCssVars); only this bar and the action buttons
          follow the staff v2 tokens. */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-lg bg-surface px-4 py-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-ink">{template.name}</h1>
          <p className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm ${FORMS_MUTED}`}>
            {currentInstance.member_name && (
              <span>{t('formsEditor.member', 'Member: {{name}}', { name: currentInstance.member_name })}</span>
            )}
            <span className="inline-flex items-center gap-1.5">
              {t('formsEditor.status', 'Status')}
              {/* `billed_qa` must not render as "Billed qa" — the billing states get real labels. */}
              <Badge variant={statusVariant} size="sm" className={isBilled ? '' : 'capitalize'}>
                {statusLabel}
              </Badge>
            </span>
            {!isReadOnly && (
              <span className={isDirty ? 'text-attention-ink' : 'text-success-ink'}>
                {isDirty
                  ? t('formsEditor.unsaved', 'Unsaved changes')
                  : t('formsEditor.all_saved', 'All changes saved')}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Draft-only actions. During an amendment these must NOT show: "Save Draft"
              would reopen a submitted record as a draft, and "Submit Form" would re-submit
              it. The amendment banner owns saving in that mode. */}
          {!isSubmittedRecord && (
            <>
              <Button
                variant="secondary"
                className={FORMS_SECONDARY_BTN}
                onClick={() => handleSaveDraft(false)}
                disabled={saving || !isDirty}
              >
                <Save className="w-4 h-4" />
                {saving ? t('formsEditor.saving', 'Saving...') : t('formsEditor.save_draft', 'Save Draft')}
              </Button>
              <Button variant="default" className={FORMS_PRIMARY_BTN} onClick={handleSubmit} disabled={submitting}>
                <Send className="w-4 h-4" />
                {submitting ? t('formsEditor.submitting', 'Submitting...') : t('formsEditor.submit', 'Submit Form')}
              </Button>
            </>
          )}
          {canSaveNoteOnly && (
            <Button
              variant="default"
              className={FORMS_PRIMARY_BTN}
              onClick={handleSaveNote}
              disabled={saving || !isDirty}
              title={t('formsEditor.save_note_help', 'Save the note. The billing on this record has already been submitted and is unchanged.')}
            >
              <Save className="w-4 h-4" />
              {saving ? t('formsEditor.saving', 'Saving...') : t('formsEditor.save_note', 'Save Note')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={FORMS_GHOST_BTN}
            onClick={createTrackingAndPrint}
            title={t('formsEditor.print_help', 'Print form')}
          >
            <Printer className="w-3.5 h-3.5" />
            {t('formsEditor.print', 'Print')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={FORMS_GHOST_BTN}
            onClick={handleDownloadForm}
            title={t('formsEditor.download_help', 'Download as PDF')}
          >
            <Download className="w-3.5 h-3.5" />
            {t('formsEditor.download', 'Download')}
          </Button>
          {instanceId && (
            <Button variant="ghost" size="sm" className={FORMS_GHOST_BTN} onClick={loadHistory}>
              <Clock className="w-3.5 h-3.5" />
              {t('forms.history', 'History')}
            </Button>
          )}
          {onClose && (
            <Button variant="secondary" className={FORMS_SECONDARY_BTN} onClick={onClose}>
              <X className="w-4 h-4" />
              {t('forms.close_1', 'Close')}
            </Button>
          )}
        </div>
      </div>

      {/* Hard-copy tracking alert */}
      {trackingAlert && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-v2border bg-success-soft px-4 py-3 text-sm font-medium text-success-ink">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {trackingAlert}
          {trackingNumber && (
            <span className="ml-1 font-mono text-base font-bold">{trackingNumber}</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={`ml-auto h-7 w-7 ${FORMS_GHOST_BTN}`}
            onClick={() => setTrackingAlert(null)}
            aria-label={t('forms.close', 'Close')}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Hard-copy warning: form does not have tracking enabled */}
      {hardCopyWarning && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-v2border bg-attention-soft px-4 py-3 text-sm text-attention-ink">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div>
            <strong>{t('formsEditor.no_tracking', 'Hard-copy tracking is not enabled for this form.')}</strong>
            <span className="mt-0.5 block text-xs">
              {t('formsEditor.no_tracking_help', 'No tracking number will be generated. To enable, edit this form template in the Form Builder and check "Requires hard-copy tracking" in settings.')}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className={`ml-auto h-7 w-7 ${FORMS_GHOST_BTN}`}
            onClick={() => setHardCopyWarning(false)}
            aria-label={t('forms.close', 'Close')}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-v2border bg-danger-soft px-4 py-3 text-sm text-danger-ink">
          <strong>{t('forms.error', 'Error:')}</strong> {error}
        </div>
      )}

      {isReadOnly && (
        <div
          className={`mb-3 rounded-lg border border-v2border px-4 py-3 text-sm ${
            isBilled ? 'bg-attention-soft text-attention-ink' : 'bg-v2accent-soft text-v2accent-ink'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>
                {isBilled
                  ? t('forms.billed_readonly', '{{status}} — read-only:', {
                      status: statusLabel,
                    })
                  : t('forms.readonly', 'Read-only:')}
              </strong>{' '}
              {isBilled
                ? canAmendThis
                  ? t('forms.billed_amendable', 'This note has been billed. You can correct it during QA — an audit note explaining the change is required.')
                  : t('forms.billed_locked', 'This note has been billed and is locked. Correcting a billed note needs the ‘Amend billed notes’ permission.')
                : canAmendThis
                  ? t('forms.submitted_amendable', 'This form has been submitted. You can amend it — the change will be recorded in its history.')
                  : t('forms.this_form_has_been_submitted', 'This form has been submitted and cannot be edited.')}
            </span>
            <span className="flex flex-wrap gap-2">
              {canAmendThis && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className={FORMS_SECONDARY_BTN}
                  onClick={() => setAmending(true)}
                >
                  <PenTool className="w-3.5 h-3.5" /> {isBilled ? t('forms.correct', 'Correct') : t('forms.amend', 'Amend')}
                </Button>
              )}
              {/* Billing cycle — billing/supervisory staff mark a note reported without
                  touching a clinical field. Gated on forms.set_billing_status. */}
              {perms?.can_set_billing_status && (
                <>
                  {!isBilled && (
                    <Button type="button" variant="secondary" size="sm" className={FORMS_SECONDARY_BTN} disabled={billingBusy}
                      onClick={() => changeBillingStatus('billed')}
                      title={t('forms.mark_billed_help', 'Mark this note as reported/billed to the plan')}>
                      {t('forms.mark_billed', 'Mark Billed')}
                    </Button>
                  )}
                  {(currentInstance.status || '').toLowerCase() === 'billed' && (
                    <Button type="button" variant="secondary" size="sm" className={FORMS_SECONDARY_BTN} disabled={billingBusy}
                      onClick={() => changeBillingStatus('billed_qa')}
                      title={t('forms.mark_qa_help', 'Put this billed note into post-audit QA')}>
                      {t('forms.mark_billed_qa', 'Move to Billed + QA')}
                    </Button>
                  )}
                  {isBilled && (
                    <Button type="button" variant="secondary" size="sm" className={FORMS_SECONDARY_BTN} disabled={billingBusy}
                      onClick={() => changeBillingStatus('submitted')}
                      title={t('forms.unbill_help', 'Take this note back out of the billing cycle')}>
                      {t('forms.unbill', 'Back to Submitted')}
                    </Button>
                  )}
                </>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Amending an already-submitted record. The reason is stored on the history row so a
          later reader can see why a signed record changed, not just that it did. */}
      {isSubmittedRecord && amending && (
        <div className="mb-3 rounded-lg border border-v2border bg-attention-soft px-4 py-3 text-sm text-attention-ink">
          <div>
            <strong>
              {isBilled
                ? t('forms.correcting_billed', 'Correcting a billed note.')
                : t('forms.amending', 'Amending a submitted form.')}
            </strong>{' '}
            {isBilled
              ? t('forms.correcting_billed_help', 'This note has already been billed, so the audit note below is required and is kept with the change. The note stays in the billing cycle.')
              : t('forms.amending_help', 'Your changes, and your name, are recorded in this form’s history. The form stays submitted.')}
          </div>
          {/* Both tiers require a reason now, so the label distinguishes the TIER rather than
              required-vs-optional: a billed note's reason is an audit note on a claim, a
              submitted note's is why a signed record changed. */}
          <label className="block mt-2 text-xs font-medium">
            {isBilled
              ? t('forms.audit_note_required', 'Audit note — required')
              : t('forms.amendment_reason_required', 'Reason for amendment — required')}
            <input
              type="text"
              value={amendmentReason}
              onChange={(e) => setAmendmentReason(e.target.value)}
              placeholder={isBilled
                ? t('forms.audit_note_ph', 'e.g. corrected units per MCP claim response')
                : t('forms.amendment_reason_ph', 'e.g. corrected the visit date')}
              className={`mt-1 w-full px-2 py-1.5 text-sm rounded border bg-surface text-ink ${
                reasonRequired && !amendmentReason.trim() ? 'border-danger' : 'border-v2border'
              }`}
            />
          </label>
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              variant="default"
              size="sm"
              className={FORMS_PRIMARY_BTN}
              onClick={handleSaveAmendment}
              disabled={saving || !isDirty || (reasonRequired && !amendmentReason.trim())}
              title={
                !isDirty
                  ? t('forms.no_changes_yet', 'Make a change first')
                  : reasonRequired && !amendmentReason.trim()
                    ? isBilled
                      ? t('forms.audit_note_needed', 'An audit note is required to correct a billed note')
                      : t('forms.reason_needed', 'A reason is required to amend a submitted form')
                    : undefined
              }
            >
              <Save className="w-3.5 h-3.5" /> {saving ? t('forms.saving', 'Saving…') : t('forms.save_amendment', 'Save amendment')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={FORMS_SECONDARY_BTN}
              onClick={() => { setAmending(false); setAmendmentReason(''); }}
              disabled={saving}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {!isReadOnly && (
        <div className="mb-5 rounded-lg bg-surface px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <strong className="text-sm text-ink">{t('forms.completion_progress', 'Completion Progress')}</strong>
            <span className={`text-sm tabular-nums ${FORMS_MUTED}`}>{completionPercentage}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded bg-paper-2">
            <div
              className={`h-full transition-[width] duration-300 ${completionPercentage === 100 ? 'bg-success' : 'bg-v2accent'}`}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          {!isReadOnly && (
            <div className={`mt-2 flex flex-wrap items-center gap-2 text-xs ${FORMS_MUTED}`}>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={autoSave.enabled}
                  disabled={savingPreference}
                  onChange={(e) => saveAutoSavePreference({ form_autosave_enabled: e.target.checked })}
                />
                {autoSave.enabled
                  ? t('forms.autosave_every', 'Auto-save every')
                  : t('forms.autosave_off', 'Auto-save is off')}
              </label>

              {autoSave.enabled && (
                <select
                  aria-label={t('forms.autosave_interval_label', 'Auto-save frequency')}
                  className="rounded border border-v2border bg-surface px-1 py-px text-xs text-ink"
                  value={String(autoSave.seconds)}
                  disabled={savingPreference}
                  onChange={(e) =>
                    saveAutoSavePreference({ form_autosave_interval_seconds: Number(e.target.value) })
                  }
                >
                  {/* A template default that is not one of the presets still has to be
                      selectable, or changing the dropdown would be the only way to get
                      back to the value the admin chose. */}
                  {!AUTOSAVE_INTERVAL_CHOICES.some((c) => c.seconds === autoSave.seconds) && (
                    <option value={String(autoSave.seconds)}>
                      {describeAutoSaveInterval(autoSave.seconds, t)}
                    </option>
                  )}
                  {AUTOSAVE_INTERVAL_CHOICES.map((choice) => (
                    <option key={choice.seconds} value={String(choice.seconds)}>
                      {t(choice.labelKey, choice.labelFallback)}
                    </option>
                  ))}
                </select>
              )}

              {autoSave.source !== 'user' && autoSave.enabled && (
                <span className="italic">
                  {t('forms.autosave_from_template', 'set by your organization')}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form Sections */}
      {currentInstance && currentInstance.status !== 'draft' ? (
        // Submitted/reviewed/approved/locked → the new FormRenderer. Editability must
        // follow `isReadOnly` (= isSubmittedRecord && !amending), the same flag the
        // banner, Save-amendment button and status indicator already honor. Passing a
        // bare `disabled` here kept every field greyed out even after the user chose to
        // amend and entered a reason — so no field could change, isDirty never flipped,
        // and "Save amendment" stayed disabled (TRUS-499). Wire onChange so amend-mode
        // edits update the payload and mark the record dirty.
        <div className="rounded-xl bg-surface p-6">
          <FormRenderer
            sections={template.sections}
            value={fieldData}
            onChange={handleFieldChange}
            disabled={isReadOnly}
            // The record is read-only, but a field flagged editable-after-submit is not:
            // the LCM submits the billing so the claim goes out, then finishes the
            // narrative (TRUS-566 #3). Only while NOT amending — in amend mode the whole
            // record is already open and the amendment gate is doing its job.
            unlockedFieldIds={amending ? undefined : postSubmitFieldIds.filter((id) => !lockedSignatureDates.has(id))}
            theme={formTheme}
            // TRUS-763: a submitted plan can still be signed (IFR finalizes, then signs).
            onAddSignature={
              isReadOnly && (currentInstance.status || '').toLowerCase() !== 'voided' ? openSignaturePad : undefined
            }
          />
        </div>
      ) : (
      <div className="rounded-xl bg-surface p-6">
        {/* Suggested needs (TRUS-576). Shown on BOTH documents — the assessment,
            where the evidence was gathered, and the care plan, where the need will
            live. Above the sections because it is a prompt to act, not a field.

            A need is written into a CARE PLAN, so accepting is only possible when
            the open form IS one. On an assessment the panel still shows the
            evidence and says where to go — which is better than hiding it, since
            the assessment is where the LCM is looking when the evidence is fresh. */}
        {memberId && (
          <NeedSuggestions
            memberId={memberId}
            carePlanInstanceId={template.form_type === 'care_plan' ? currentInstance?.id ?? null : null}
            context={template.form_type === 'care_plan' ? 'care_plan' : 'assessment'}
            onAccepted={() => loadInstance()}
          />
        )}
        {template.sections.map((section, sectionIndex) => {
          const isCollapsed = collapsedSections.has(section.section_id);

          return (
            <div
              key={section.section_id}
              className="border-gray-200 dark:border-gray-700"
              style={{
                marginBottom: '20px',
                border: '1px solid',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              {/* Section Header */}
              <div
                className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                style={{
                  padding: '16px',
                  borderBottom: isCollapsed ? 'none' : '1px solid',
                  cursor: section.collapsible ? 'pointer' : 'default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                onClick={() => section.collapsible && toggleSection(section.section_id)}
              >
                <div>
                  <h3 className="text-gray-900 dark:text-white" style={{ margin: 0, fontSize: '16px' }}>{section.title}</h3>
                  {section.description && (
                    <p className="text-gray-600 dark:text-gray-400" style={{ margin: '5px 0 0 0', fontSize: '13px' }}>{section.description}</p>
                  )}
                </div>
                {section.collapsible && (
                  <div>
                    {isCollapsed ? (
                      <Eye className="w-5 h-5 text-gray-400" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                )}
              </div>

              {/* Section Fields */}
              {!isCollapsed && (
                <div style={{ padding: '20px' }}>
                  {/* 12-column grid, so a field's stored `width` lays out while
                      the note is being WRITTEN and not only once it is
                      submitted. This was already a grid — pinned to a single
                      1fr column — so widening it is the whole change.
                      Gaps follow the design template's `spacing` when the form
                      is themed, and fall back to what the old marginBottom:15px
                      on each field used to give. */}
                  <div
                    className="grid grid-cols-12"
                    style={
                      themed
                        ? { rowGap: 'var(--wf-row-gap)', columnGap: 'var(--wf-col-gap)' }
                        : { rowGap: '15px', columnGap: '1rem' }
                    }
                  >
                    {section.fields.map((field) => {
                      const node = renderField(field);
                      // renderField returns null for a field hidden by its
                      // visibility rules. The old code still rendered an empty
                      // <div> for it — invisible in one column, but in a real
                      // grid it would hold a cell open and shove the next field
                      // sideways.
                      if (!node) return null;
                      return (
                        <div
                          key={field.field_id}
                          id={`field-${field.field_id}`}
                          className={spanClassFor(field as any)}
                        >
                          {node}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Billing Capture Section - Only shown for billing form types */}
      {template.form_type === 'billing' && memberId && programId && organizationId && (
        <div className="mt-5 rounded-xl bg-surface p-6">
          <div
            className="border-gray-200 dark:border-gray-700"
            style={{
              border: '1px solid',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div
              className="bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700"
              style={{
                padding: '16px',
                borderBottom: '1px solid',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <div>
                <h3 className="text-gray-900 dark:text-white" style={{ margin: 0, fontSize: '16px' }}>{t('forms.billing_information', 'Billing Information')}</h3>
                <p className="text-gray-600 dark:text-gray-400" style={{ margin: '5px 0 0 0', fontSize: '13px' }}>
                  {t('forms.capture_billing_details_for_this', 'Capture billing details for this service')}
                </p>
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <BillingCaptureForm
                memberId={memberId}
                programId={programId}
                organizationId={organizationId}
                workflowType="form"
                workflowId={currentInstance?.id}
                autoPopulateFromWorkflow={true}
                defaultData={{
                  service_date_from: new Date().toISOString().split('T')[0],
                  service_date_to: new Date().toISOString().split('T')[0],
                }}
                onSave={async (data, asDraft) => {
                  // The billing capture form handles its own save
                  // After billing is captured, close the form if requested
                  if (!asDraft && onClose) {
                    onClose();
                  }
                }}
                onCancel={onClose}
              />
            </div>
          </div>
        </div>
      )}

      {/* History Modal — z-index above sidebar, centered in viewport */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)} style={{ zIndex: 9999, position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div
            className="relative mx-auto w-[90%] max-w-[550px] rounded-xl border border-v2border bg-surface text-ink shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-v2border px-4 py-3">
              <h2 className="text-base font-semibold text-ink">{t('forms.edit_history', 'Edit History')}</h2>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${FORMS_GHOST_BTN}`}
                onClick={() => setShowHistory(false)}
                aria-label={t('forms.close', 'Close')}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {history.length > 0 ? (
                history.map((entry, index) => (
                  <div
                    key={index}
                    className="border-b border-v2border px-4 py-3"
                  >
                    <div className="text-[13px] text-ink">
                      <strong>{entry.action}</strong>
                    </div>
                    <div className={`mt-0.5 text-xs ${FORMS_MUTED}`}>
                      {fmt.dateTime(entry.timestamp)}
                      {entry.user_name && ` • ${t('formsEditor.by_user', 'by {{name}}', { name: entry.user_name })}`}
                    </div>
                  </div>
                ))
              ) : (
                <div className={`p-5 text-center text-sm ${FORMS_MUTED}`}>
                  {t('forms.no_history_available', 'No history available')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Signature Pad Dialog for form signature fields */}
      <Dialog open={formSignatureDialogOpen} onOpenChange={setFormSignatureDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('formsEditor.esignature', 'Electronic Signature')}</DialogTitle>
          </DialogHeader>
          <SignaturePad
            key={formSignatureFieldId || 'none'}
            // TRUS-763: the name used to start empty, and Sign stayed disabled until
            // someone typed it, which read as "the pad doesn't work". Prefilled with the
            // member for a member signature, else the logged-in staff member; editable.
            signerName={signatureFieldBeingSigned ? defaultSignerName(signatureFieldBeingSigned, {
              memberName: currentInstance?.member_name,
              staffName: auth?.user?.name,
            }) : ''}
            title={t('formsEditor.sign_this_form', 'Sign this form')}
            message={t('formsEditor.sign_message', 'Your signature will be captured and attached to this form.')}
            onSign={(result: SignatureResult) => {
              if (formSignatureFieldId) {
                void saveSignature(formSignatureFieldId, result);
              }
              setFormSignatureDialogOpen(false);
              setFormSignatureFieldId(null);
            }}
            onCancel={() => { setFormSignatureDialogOpen(false); setFormSignatureFieldId(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
    </FormProvider>
  );
};

export default FormInstanceEditor;
